import { useCallback, useEffect, useRef } from 'react';

/**
 * Trailing-edge debounce, the subset of `use-debounce`'s `useDebouncedCallback` the
 * ported search inputs rely on. The timer is cleared on unmount so a pending call never
 * fires against a gone component.
 */
export function useDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void,
  wait: number,
) {
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(callback);

  useEffect(() => {
    latest.current = callback;
  }, [callback]);

  useEffect(
    () => () => {
      if (timeout.current) clearTimeout(timeout.current);
    },
    [],
  );

  return useCallback(
    (...args: Args) => {
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(() => latest.current(...args), wait);
    },
    [wait],
  );
}
